import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

interface MarkdownRendererProps {
  content: string
  className?: string
}

export function MarkdownRenderer({ content, className = '' }: MarkdownRendererProps) {
  return (
    <div className={`prose prose-sm max-w-none dark:prose-invert ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
      components={{
        // Customize heading styles
        h1: ({ children }) => <h1 className="text-lg font-bold mb-2 mt-4 first:mt-0">{children}</h1>,
        h2: ({ children }) => <h2 className="text-base font-semibold mb-2 mt-3 first:mt-0">{children}</h2>,
        h3: ({ children }) => <h3 className="text-sm font-medium mb-1 mt-2 first:mt-0">{children}</h3>,
        
        // Customize paragraph styles
        p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
        
        // Customize list styles
        ul: ({ children }) => <ul className="mb-2 pl-4 space-y-1">{children}</ul>,
        ol: ({ children }) => <ol className="mb-2 pl-4 space-y-1">{children}</ol>,
        li: ({ children }) => <li className="text-sm">{children}</li>,
        
        // Customize code styles
        code: ({ children, className }) => {
          const isInline = !className
          if (isInline) {
            return (
              <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">
                {children}
              </code>
            )
          }
          return (
            <code className="block bg-muted p-3 rounded-lg text-xs font-mono overflow-x-auto">
              {children}
            </code>
          )
        },
        
        // Customize pre styles (code blocks)
        pre: ({ children }) => (
          <pre className="bg-muted p-3 rounded-lg overflow-x-auto mb-2">
            {children}
          </pre>
        ),
        
        // Customize blockquote styles
        blockquote: ({ children }) => (
          <blockquote className="border-l-4 border-muted-foreground/20 pl-4 py-2 mb-2 italic">
            {children}
          </blockquote>
        ),
        
        // Customize table styles
        table: ({ children }) => (
          <div className="overflow-x-auto mb-2">
            <table className="min-w-full border-collapse border border-muted">
              {children}
            </table>
          </div>
        ),
        th: ({ children }) => (
          <th className="border border-muted bg-muted/50 px-2 py-1 text-left text-xs font-medium">
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className="border border-muted px-2 py-1 text-xs">
            {children}
          </td>
        ),
        
        // Customize link styles
        a: ({ children, href }) => (
          <a 
            href={href} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            {children}
          </a>
        ),
        
        // Customize horizontal rule
        hr: () => <hr className="border-muted my-4" />
      }}
    >
      {content}
    </ReactMarkdown>
    </div>
  )
} 